import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useMarkInvoicePaid, type MarkInvoicePaidMethod } from '@/hooks/useInvoices';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDateInputInTz } from '@/utils/timezone';

const schema = z.object({
  payment_method: z.enum(['cash', 'card', 'bank_transfer', 'other']),
  payment_date: z.string().min(1, 'Payment date is required'),
  notes: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

const METHOD_OPTIONS: Array<{ value: MarkInvoicePaidMethod; label: string }> = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card / EFTPOS' },
  { value: 'other', label: 'Other' },
];

interface MarkInvoicePaidDialogProps {
  invoice: {
    id: string;
    invoice_number: string;
    total_amount: number;
    amount_paid?: number | null;
    job_id?: string | null;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the invoice is marked paid. */
  onDone?: () => void;
}

/**
 * Office "Mark as Paid". The outstanding balance is recorded as a payment on
 * the job (so the ledger, the job and the invoice agree, and the accounting
 * sync has a payment to post); the user says how the money arrived.
 */
export function MarkInvoicePaidDialog({ invoice, open, onOpenChange, onDone }: MarkInvoicePaidDialogProps) {
  const markPaid = useMarkInvoicePaid();
  const { formatMoney, timezone } = useCompanyLocale();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      payment_method: 'bank_transfer',
      payment_date: formatDateInputInTz(new Date(), timezone),
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        payment_method: 'bank_transfer',
        payment_date: formatDateInputInTz(new Date(), timezone),
        notes: '',
      });
    }
  }, [open, timezone, form]);

  const outstanding = invoice ? Math.max(Number(invoice.total_amount ?? 0) - Number(invoice.amount_paid ?? 0), 0) : 0;
  const busy = markPaid.isPending;

  const onSubmit = async (values: FormValues) => {
    if (!invoice) return;
    try {
      await markPaid.mutateAsync({
        invoiceId: invoice.id,
        paymentMethod: values.payment_method,
        paidDate: values.payment_date,
        notes: values.notes,
      });
      onOpenChange(false);
      onDone?.();
    } catch {
      // Toast raised by the hook; keep the dialog open so the user can retry or back out.
    }
  };

  return (
    <Dialog open={open && !!invoice} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mark {invoice?.invoice_number ?? 'invoice'} as paid</DialogTitle>
          <DialogDescription>
            {invoice?.job_id
              ? `The outstanding ${formatMoney(outstanding)} is recorded as a payment on the job.`
              : `The invoice is marked paid in full (${formatMoney(outstanding)} outstanding).`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="payment_method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How was it paid?</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {METHOD_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="payment_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note (optional)</FormLabel>
                  <FormControl>
                    <Textarea rows={2} placeholder="Reference, receipt number…" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mark as paid
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
